/**
 * Permission Constants
 *
 * ~170 granular permissions in product:action format, matching the backend
 * registry in admin-user-service/internal/permissions/registry.go exactly.
 */

// ─── Permission constants ────────────────────────────────────────────────────

export const PERMISSIONS = {
    // Customers (5)
    CUSTOMERS_VIEW: 'customers:view',
    CUSTOMERS_CREATE: 'customers:create',
    CUSTOMERS_EDIT: 'customers:edit',
    CUSTOMERS_DELETE: 'customers:delete',
    CUSTOMERS_EXPORT: 'customers:export',

    // Accounts (5)
    ACCOUNTS_VIEW: 'accounts:view',
    ACCOUNTS_CREATE: 'accounts:create',
    ACCOUNTS_EDIT: 'accounts:edit',
    ACCOUNTS_CLOSE: 'accounts:close',
    ACCOUNTS_FREEZE: 'accounts:freeze',

    // Transactions (5)
    TRANSACTIONS_VIEW: 'transactions:view',
    TRANSACTIONS_CREATE: 'transactions:create',
    TRANSACTIONS_REVERSE: 'transactions:reverse',
    TRANSACTIONS_APPROVE: 'transactions:approve',
    TRANSACTIONS_EXPORT: 'transactions:export',

    // Cards (4)
    CARDS_VIEW: 'cards:view',
    CARDS_CREATE: 'cards:create',
    CARDS_MANAGE: 'cards:manage',
    CARDS_BLOCK: 'cards:block',

    // Loans (5)
    LOANS_VIEW: 'loans:view',
    LOANS_CREATE: 'loans:create',
    LOANS_APPROVE: 'loans:approve',
    LOANS_MANAGE: 'loans:manage',
    LOANS_DISBURSE: 'loans:disburse',

    // Savings / Deposits (3)
    SAVINGS_VIEW: 'savings:view',
    SAVINGS_CREATE: 'savings:create',
    SAVINGS_MANAGE: 'savings:manage',

    // Users — admin panel user management (5)
    USERS_VIEW: 'users:view',
    USERS_CREATE: 'users:create',
    USERS_EDIT: 'users:edit',
    USERS_DELETE: 'users:delete',
    USERS_ASSIGN_ROLES: 'users:assign-roles',

    // Reports (4)
    REPORTS_VIEW: 'reports:view',
    REPORTS_CREATE: 'reports:create',
    REPORTS_EXPORT: 'reports:export',
    REPORTS_SCHEDULE: 'reports:schedule',

    // Settings (3)
    SETTINGS_VIEW: 'settings:view',
    SETTINGS_EDIT: 'settings:edit',
    SETTINGS_SECURITY: 'settings:security',

    // Audit (2)
    AUDIT_VIEW: 'audit:view',
    AUDIT_EXPORT: 'audit:export',

    // General Ledger (4)
    GL_VIEW: 'gl:view',
    GL_MANAGE: 'gl:manage',
    GL_JOURNALS_CREATE: 'gl:journals:create',
    GL_JOURNALS_APPROVE: 'gl:journals:approve',

    // Branch Banking (4)
    BRANCH_BANKING_ACCESS: 'branch:access',
    BRANCH_TRANSACTIONS_CREATE: 'branch:transactions:create',
    BRANCH_ACCOUNTS_OPEN: 'branch:accounts:open',
    BRANCH_CASH_MANAGE: 'branch:cash:manage',

    // Compliance (4)
    COMPLIANCE_VIEW: 'compliance:view',
    COMPLIANCE_MANAGE: 'compliance:manage',
    COMPLIANCE_SAR_FILE: 'compliance:sar:file',
    COMPLIANCE_SANCTIONS_SCREEN: 'compliance:sanctions:screen',

    // Payment Operations (4)
    PAYMENTS_VIEW: 'payments:view',
    PAYMENTS_MANAGE: 'payments:manage',
    PAYMENTS_RECONCILE: 'payments:reconcile',
    PAYMENTS_SCHEME_CONFIG: 'payments:scheme:config',

    // Platform — OneGroup internal only (8)
    PLATFORM_INSTITUTIONS_CREATE: 'platform:institutions:create',
    PLATFORM_INSTITUTIONS_MANAGE: 'platform:institutions:manage',
    PLATFORM_ROUTES_MANAGE: 'platform:routes:manage',
    PLATFORM_SUPPORT_ACCESS: 'platform:support:access',
    PLATFORM_SYSTEM_CONFIG: 'platform:system:config',
    PLATFORM_HEALTH_VIEW: 'platform:health:view',
    PLATFORM_METERING_VIEW: 'platform:metering:view',
    PLATFORM_BILLING_MANAGE: 'platform:billing:manage',

    // Mortgages (3)
    MORTGAGE_VIEW: 'mortgages:view',
    MORTGAGE_MANAGE: 'mortgages:manage',
    MORTGAGE_APPROVE: 'mortgages:approve',

    // Neural / AI (9)
    NEURAL_PERSONALITY_VIEW: 'neural:personality:view',
    NEURAL_PERSONALITY_MANAGE: 'neural:personality:manage',
    NEURAL_RULES_VIEW: 'neural:rules:view',
    NEURAL_RULES_MANAGE: 'neural:rules:manage',
    NEURAL_FEEDBACK_VIEW: 'neural:feedback:view',
    NEURAL_SESSIONS_VIEW: 'neural:sessions:view',
    NEURAL_SESSIONS_MANAGE: 'neural:sessions:manage',
    NEURAL_AGENTS_CONFIG: 'neural:agents:config',
    NEURAL_MODELS_MANAGE: 'neural:models:manage',

    // Insights (6)
    INSIGHTS_RULES_VIEW: 'insights:rules:view',
    INSIGHTS_RULES_DESIGN: 'insights:rules:design',
    INSIGHTS_RULES_MANAGE: 'insights:rules:manage',
    INSIGHTS_SCHEDULES_VIEW: 'insights:schedules:view',
    INSIGHTS_SCHEDULES_MANAGE: 'insights:schedules:manage',
    INSIGHTS_ANALYTICS_VIEW: 'insights:analytics:view',

    // Profiles (5)
    PROFILES_TRAITS_VIEW: 'profiles:traits:view',
    PROFILES_TRAITS_DESIGN: 'profiles:traits:design',
    PROFILES_TRAITS_MANAGE: 'profiles:traits:manage',
    PROFILES_OVERRIDES_VIEW: 'profiles:overrides:view',
    PROFILES_OVERRIDES_MANAGE: 'profiles:overrides:manage',

    // Canonical institution actions
    INSTITUTION_INSIGHTS_VIEW: 'institution:insights:view',
    INSTITUTION_INSIGHTS_MANAGE: 'institution:insights:manage',
    INSTITUTION_REWARDS_VIEW: 'institution:rewards:view',
    INSTITUTION_REWARDS_MANAGE: 'institution:rewards:manage',
    INSTITUTION_JOURNEYS_VIEW: 'institution:journeys:view',
    INSTITUTION_JOURNEYS_MANAGE: 'institution:journeys:manage',
    INSTITUTION_NOTIFICATIONS_VIEW: 'institution:notifications:view',
    INSTITUTION_NOTIFICATIONS_MANAGE: 'institution:notifications:manage',
    INSTITUTION_PROFILE_TRAITS_VIEW: 'institution:profile-traits:view',
    INSTITUTION_PROFILE_TRAITS_MANAGE: 'institution:profile-traits:manage',
    INSTITUTION_MONI_FEEDBACK_VIEW: 'institution:moni-feedback:view',

    // Journeys (4)
    JOURNEYS_TEMPLATES_VIEW: 'journeys:templates:view',
    JOURNEYS_TEMPLATES_MANAGE: 'journeys:templates:manage',
    JOURNEYS_TEMPLATES_PUBLISH: 'journeys:templates:publish',
    JOURNEYS_VERSIONS_VIEW: 'journeys:versions:view',

    // Providers (7)
    PROVIDERS_CATALOG_VIEW: 'providers:catalog:view',
    PROVIDERS_CATALOG_MANAGE: 'providers:catalog:manage',
    PROVIDERS_CONTRACTS_VIEW: 'providers:contracts:view',
    PROVIDERS_CONTRACTS_MANAGE: 'providers:contracts:manage',
    PROVIDERS_HEALTH_VIEW: 'providers:health:view',
    PROVIDERS_SWAP: 'providers:swap',
    PROVIDERS_METERING_VIEW: 'providers:metering:view',

    // Fees (5)
    FEES_DEFINITIONS_VIEW: 'fees:definitions:view',
    FEES_DEFINITIONS_MANAGE: 'fees:definitions:manage',
    FEES_SCHEDULES_VIEW: 'fees:schedules:view',
    FEES_SCHEDULES_MANAGE: 'fees:schedules:manage',
    FEES_SIMULATE: 'fees:simulate',

    // Rewards (11)
    REWARDS_CONFIG_VIEW: 'rewards:config:view',
    REWARDS_CONFIG_MANAGE: 'rewards:config:manage',
    REWARDS_SCHEMES_VIEW: 'rewards:schemes:view',
    REWARDS_SCHEMES_DESIGN: 'rewards:schemes:design',
    REWARDS_SCHEMES_MANAGE: 'rewards:schemes:manage',
    REWARDS_LOOKUPS_VIEW: 'rewards:lookups:view',
    REWARDS_LOOKUPS_MANAGE: 'rewards:lookups:manage',
    REWARDS_COINS_VIEW: 'rewards:coins:view',
    REWARDS_COINS_ADJUST: 'rewards:coins:adjust',
    REWARDS_BADGES_MANAGE: 'rewards:badges:manage',
    REWARDS_MILESTONES_MANAGE: 'rewards:milestones:manage',

    // Open Banking (7)
    OPENBANKING_CONFIG_VIEW: 'openbanking:config:view',
    OPENBANKING_CONFIG_MANAGE: 'openbanking:config:manage',
    OPENBANKING_CONNECTIONS_VIEW: 'openbanking:connections:view',
    OPENBANKING_ALERTS_VIEW: 'openbanking:alerts:view',
    OPENBANKING_ALERTS_MANAGE: 'openbanking:alerts:manage',
    OPENBANKING_STATS_VIEW: 'openbanking:stats:view',
    OPENBANKING_CONSENTS_VIEW: 'openbanking:consents:view',

    // Products (8)
    PRODUCTS_DEFINITIONS_VIEW: 'products:definitions:view',
    PRODUCTS_DEFINITIONS_MANAGE: 'products:definitions:manage',
    PRODUCTS_VERSIONS_VIEW: 'products:versions:view',
    PRODUCTS_VERSIONS_MANAGE: 'products:versions:manage',
    PRODUCTS_FEES_MANAGE: 'products:fees:manage',
    PRODUCTS_INTEREST_MANAGE: 'products:interest:manage',
    PRODUCTS_ACTIVATE: 'products:activate',
    PRODUCTS_DEACTIVATE: 'products:deactivate',

    // Limits (3)
    LIMITS_VIEW: 'limits:view',
    LIMITS_MANAGE: 'limits:manage',
    LIMITS_OVERRIDE: 'limits:override',

    // KYB (10)
    KYB_ENTITIES_VIEW: 'kyb:entities:view',
    KYB_ENTITIES_MANAGE: 'kyb:entities:manage',
    KYB_REVIEWS_VIEW: 'kyb:reviews:view',
    KYB_REVIEWS_MANAGE: 'kyb:reviews:manage',
    KYB_DOCUMENTS_VIEW: 'kyb:documents:view',
    KYB_DOCUMENTS_MANAGE: 'kyb:documents:manage',
    KYB_SCREENING_VIEW: 'kyb:screening:view',
    KYB_SCREENING_MANAGE: 'kyb:screening:manage',
    KYB_CONFIG_VIEW: 'kyb:config:view',
    KYB_CONFIG_MANAGE: 'kyb:config:manage',

    // Regulatory (7)
    REGULATORY_REPORTS_VIEW: 'regulatory:reports:view',
    REGULATORY_REPORTS_MANAGE: 'regulatory:reports:manage',
    REGULATORY_SUBMISSIONS_VIEW: 'regulatory:submissions:view',
    REGULATORY_SUBMISSIONS_MANAGE: 'regulatory:submissions:manage',
    REGULATORY_CAPITAL_VIEW: 'regulatory:capital:view',
    REGULATORY_CAPITAL_MANAGE: 'regulatory:capital:manage',
    REGULATORY_CONFIG_MANAGE: 'regulatory:config:manage',

    // Config (5)
    CONFIG_VIEW: 'config:view',
    CONFIG_MANAGE: 'config:manage',
    CONFIG_SEED: 'config:seed',
    CONFIG_EXPORT: 'config:export',
    CONFIG_IMPORT: 'config:import',

    // Deposits (5)
    DEPOSITS_RATES_VIEW: 'deposits:rates:view',
    DEPOSITS_RATES_MANAGE: 'deposits:rates:manage',
    DEPOSITS_PRODUCTS_MANAGE: 'deposits:products:manage',
    DEPOSITS_ACCOUNTS_MANAGE: 'deposits:accounts:manage',
    DEPOSITS_MATURITY_MANAGE: 'deposits:maturity:manage',

    // Insurance (5)
    INSURANCE_POLICIES_VIEW: 'insurance:policies:view',
    INSURANCE_POLICIES_MANAGE: 'insurance:policies:manage',
    INSURANCE_CLAIMS_VIEW: 'insurance:claims:view',
    INSURANCE_CLAIMS_MANAGE: 'insurance:claims:manage',
    INSURANCE_PRODUCTS_MANAGE: 'insurance:products:manage',

    // Standing Orders (3)
    STANDING_ORDERS_VIEW: 'standing_orders:view',
    STANDING_ORDERS_MANAGE: 'standing_orders:manage',
    STANDING_ORDERS_ADMIN: 'standing_orders:admin',

    // Notifications (4)
    NOTIFICATIONS_TEMPLATES_VIEW: 'notifications:templates:view',
    NOTIFICATIONS_TEMPLATES_MANAGE: 'notifications:templates:manage',
    NOTIFICATIONS_CHANNELS_MANAGE: 'notifications:channels:manage',
    NOTIFICATIONS_HISTORY_VIEW: 'notifications:history:view',
} as const;

// Type for permission name values
export type PermissionName = typeof PERMISSIONS[keyof typeof PERMISSIONS];

// ─── User-friendly labels ────────────────────────────────────────────────────

export const PERMISSION_LABELS: Record<PermissionName, string> = {
    // Customers
    [PERMISSIONS.CUSTOMERS_VIEW]: 'View Customers',
    [PERMISSIONS.CUSTOMERS_CREATE]: 'Create Customers',
    [PERMISSIONS.CUSTOMERS_EDIT]: 'Edit Customers',
    [PERMISSIONS.CUSTOMERS_DELETE]: 'Delete Customers',
    [PERMISSIONS.CUSTOMERS_EXPORT]: 'Export Customer Data',

    // Accounts
    [PERMISSIONS.ACCOUNTS_VIEW]: 'View Accounts',
    [PERMISSIONS.ACCOUNTS_CREATE]: 'Create Accounts',
    [PERMISSIONS.ACCOUNTS_EDIT]: 'Edit Accounts',
    [PERMISSIONS.ACCOUNTS_CLOSE]: 'Close Accounts',
    [PERMISSIONS.ACCOUNTS_FREEZE]: 'Freeze/Unfreeze Accounts',

    // Transactions
    [PERMISSIONS.TRANSACTIONS_VIEW]: 'View Transactions',
    [PERMISSIONS.TRANSACTIONS_CREATE]: 'Create Transactions',
    [PERMISSIONS.TRANSACTIONS_REVERSE]: 'Reverse Transactions',
    [PERMISSIONS.TRANSACTIONS_APPROVE]: 'Approve Transactions',
    [PERMISSIONS.TRANSACTIONS_EXPORT]: 'Export Transaction Data',

    // Cards
    [PERMISSIONS.CARDS_VIEW]: 'View Cards',
    [PERMISSIONS.CARDS_CREATE]: 'Issue New Cards',
    [PERMISSIONS.CARDS_MANAGE]: 'Manage Card Settings',
    [PERMISSIONS.CARDS_BLOCK]: 'Block/Unblock Cards',

    // Loans
    [PERMISSIONS.LOANS_VIEW]: 'View Loans',
    [PERMISSIONS.LOANS_CREATE]: 'Create Loan Applications',
    [PERMISSIONS.LOANS_APPROVE]: 'Approve/Reject Loans',
    [PERMISSIONS.LOANS_MANAGE]: 'Manage Loan Terms',
    [PERMISSIONS.LOANS_DISBURSE]: 'Disburse Approved Loans',

    // Savings
    [PERMISSIONS.SAVINGS_VIEW]: 'View Savings',
    [PERMISSIONS.SAVINGS_CREATE]: 'Create Savings Accounts',
    [PERMISSIONS.SAVINGS_MANAGE]: 'Manage Savings Products',

    // Users
    [PERMISSIONS.USERS_VIEW]: 'View Users',
    [PERMISSIONS.USERS_CREATE]: 'Create Users',
    [PERMISSIONS.USERS_EDIT]: 'Edit Users',
    [PERMISSIONS.USERS_DELETE]: 'Delete Users',
    [PERMISSIONS.USERS_ASSIGN_ROLES]: 'Assign Roles & Permissions',

    // Reports
    [PERMISSIONS.REPORTS_VIEW]: 'View Reports',
    [PERMISSIONS.REPORTS_CREATE]: 'Create Custom Reports',
    [PERMISSIONS.REPORTS_EXPORT]: 'Export Reports',
    [PERMISSIONS.REPORTS_SCHEDULE]: 'Schedule Automatic Reports',

    // Settings
    [PERMISSIONS.SETTINGS_VIEW]: 'View Settings',
    [PERMISSIONS.SETTINGS_EDIT]: 'Edit Settings',
    [PERMISSIONS.SETTINGS_SECURITY]: 'Manage Security Settings',

    // Audit
    [PERMISSIONS.AUDIT_VIEW]: 'View Audit Logs',
    [PERMISSIONS.AUDIT_EXPORT]: 'Export Audit Logs',

    // General Ledger
    [PERMISSIONS.GL_VIEW]: 'View General Ledger',
    [PERMISSIONS.GL_MANAGE]: 'Manage General Ledger',
    [PERMISSIONS.GL_JOURNALS_CREATE]: 'Create Journal Entries',
    [PERMISSIONS.GL_JOURNALS_APPROVE]: 'Approve Journal Entries',

    // Branch Banking
    [PERMISSIONS.BRANCH_BANKING_ACCESS]: 'Access Branch Banking',
    [PERMISSIONS.BRANCH_TRANSACTIONS_CREATE]: 'Create Branch Transactions',
    [PERMISSIONS.BRANCH_ACCOUNTS_OPEN]: 'Open Branch Accounts',
    [PERMISSIONS.BRANCH_CASH_MANAGE]: 'Manage Cash Drawer',

    // Compliance
    [PERMISSIONS.COMPLIANCE_VIEW]: 'View Compliance Dashboard',
    [PERMISSIONS.COMPLIANCE_MANAGE]: 'Manage Compliance Alerts',
    [PERMISSIONS.COMPLIANCE_SAR_FILE]: 'File Suspicious Activity Reports',
    [PERMISSIONS.COMPLIANCE_SANCTIONS_SCREEN]: 'Run Sanctions Screening',

    // Payment Operations
    [PERMISSIONS.PAYMENTS_VIEW]: 'View Payments',
    [PERMISSIONS.PAYMENTS_MANAGE]: 'Manage Payments',
    [PERMISSIONS.PAYMENTS_RECONCILE]: 'Run Reconciliation',
    [PERMISSIONS.PAYMENTS_SCHEME_CONFIG]: 'Configure Scheme Routing',

    // Platform
    [PERMISSIONS.PLATFORM_INSTITUTIONS_CREATE]: 'Create Institutions',
    [PERMISSIONS.PLATFORM_INSTITUTIONS_MANAGE]: 'Manage All Institutions',
    [PERMISSIONS.PLATFORM_ROUTES_MANAGE]: 'Manage Gateway Routes',
    [PERMISSIONS.PLATFORM_SUPPORT_ACCESS]: 'Access Support Tools',
    [PERMISSIONS.PLATFORM_SYSTEM_CONFIG]: 'System Configuration',
    [PERMISSIONS.PLATFORM_HEALTH_VIEW]: 'View System Health',
    [PERMISSIONS.PLATFORM_METERING_VIEW]: 'View Metering & Usage',
    [PERMISSIONS.PLATFORM_BILLING_MANAGE]: 'Manage Billing Plans',

    // Mortgages
    [PERMISSIONS.MORTGAGE_VIEW]: 'View Mortgages',
    [PERMISSIONS.MORTGAGE_MANAGE]: 'Manage Mortgages',
    [PERMISSIONS.MORTGAGE_APPROVE]: 'Approve Mortgages',

    // Neural / AI
    [PERMISSIONS.NEURAL_PERSONALITY_VIEW]: 'View AI Personality',
    [PERMISSIONS.NEURAL_PERSONALITY_MANAGE]: 'Manage AI Personality',
    [PERMISSIONS.NEURAL_RULES_VIEW]: 'View Neural Rules',
    [PERMISSIONS.NEURAL_RULES_MANAGE]: 'Manage Neural Rules',
    [PERMISSIONS.NEURAL_FEEDBACK_VIEW]: 'View AI Feedback',
    [PERMISSIONS.NEURAL_SESSIONS_VIEW]: 'View AI Sessions',
    [PERMISSIONS.NEURAL_SESSIONS_MANAGE]: 'Manage AI Sessions',
    [PERMISSIONS.NEURAL_AGENTS_CONFIG]: 'Configure AI Agents',
    [PERMISSIONS.NEURAL_MODELS_MANAGE]: 'Manage AI Models',

    // Insights
    [PERMISSIONS.INSIGHTS_RULES_VIEW]: 'View Insight Rules',
    [PERMISSIONS.INSIGHTS_RULES_DESIGN]: 'Design Insight Rules',
    [PERMISSIONS.INSIGHTS_RULES_MANAGE]: 'Manage Insight Rules',
    [PERMISSIONS.INSIGHTS_SCHEDULES_VIEW]: 'View Insight Schedules',
    [PERMISSIONS.INSIGHTS_SCHEDULES_MANAGE]: 'Manage Insight Schedules',
    [PERMISSIONS.INSIGHTS_ANALYTICS_VIEW]: 'View Insights Analytics',
    [PERMISSIONS.INSTITUTION_INSIGHTS_VIEW]: 'View Institution Insights',
    [PERMISSIONS.INSTITUTION_INSIGHTS_MANAGE]: 'Manage Institution Insights',
    [PERMISSIONS.INSTITUTION_REWARDS_VIEW]: 'View Institution Rewards',
    [PERMISSIONS.INSTITUTION_REWARDS_MANAGE]: 'Manage Institution Rewards',
    [PERMISSIONS.INSTITUTION_JOURNEYS_VIEW]: 'View Institution Journeys',
    [PERMISSIONS.INSTITUTION_JOURNEYS_MANAGE]: 'Manage Institution Journeys',
    [PERMISSIONS.INSTITUTION_NOTIFICATIONS_VIEW]: 'View Institution Notifications',
    [PERMISSIONS.INSTITUTION_NOTIFICATIONS_MANAGE]: 'Manage Institution Notifications',

    // Profiles
    [PERMISSIONS.PROFILES_TRAITS_VIEW]: 'View Profile Traits',
    [PERMISSIONS.PROFILES_TRAITS_DESIGN]: 'Design Profile Traits',
    [PERMISSIONS.PROFILES_TRAITS_MANAGE]: 'Manage Profile Traits',
    [PERMISSIONS.PROFILES_OVERRIDES_VIEW]: 'View Profile Overrides',
    [PERMISSIONS.PROFILES_OVERRIDES_MANAGE]: 'Manage Profile Overrides',
    [PERMISSIONS.INSTITUTION_PROFILE_TRAITS_VIEW]: 'View Institution Profile Traits',
    [PERMISSIONS.INSTITUTION_PROFILE_TRAITS_MANAGE]: 'Manage Institution Profile Traits',
    [PERMISSIONS.INSTITUTION_MONI_FEEDBACK_VIEW]: 'View Moni Feedback',

    // Journeys
    [PERMISSIONS.JOURNEYS_TEMPLATES_VIEW]: 'View Journey Templates',
    [PERMISSIONS.JOURNEYS_TEMPLATES_MANAGE]: 'Manage Journey Templates',
    [PERMISSIONS.JOURNEYS_TEMPLATES_PUBLISH]: 'Publish Journey Templates',
    [PERMISSIONS.JOURNEYS_VERSIONS_VIEW]: 'View Journey Versions',

    // Providers
    [PERMISSIONS.PROVIDERS_CATALOG_VIEW]: 'View Provider Catalog',
    [PERMISSIONS.PROVIDERS_CATALOG_MANAGE]: 'Manage Provider Catalog',
    [PERMISSIONS.PROVIDERS_CONTRACTS_VIEW]: 'View Provider Contracts',
    [PERMISSIONS.PROVIDERS_CONTRACTS_MANAGE]: 'Manage Provider Contracts',
    [PERMISSIONS.PROVIDERS_HEALTH_VIEW]: 'View Provider Health',
    [PERMISSIONS.PROVIDERS_SWAP]: 'Swap Providers',
    [PERMISSIONS.PROVIDERS_METERING_VIEW]: 'View Provider Metering',

    // Fees
    [PERMISSIONS.FEES_DEFINITIONS_VIEW]: 'View Fee Definitions',
    [PERMISSIONS.FEES_DEFINITIONS_MANAGE]: 'Manage Fee Definitions',
    [PERMISSIONS.FEES_SCHEDULES_VIEW]: 'View Fee Schedules',
    [PERMISSIONS.FEES_SCHEDULES_MANAGE]: 'Manage Fee Schedules',
    [PERMISSIONS.FEES_SIMULATE]: 'Simulate Fee Calculations',

    // Rewards
    [PERMISSIONS.REWARDS_CONFIG_VIEW]: 'View Rewards Config',
    [PERMISSIONS.REWARDS_CONFIG_MANAGE]: 'Manage Rewards Config',
    [PERMISSIONS.REWARDS_SCHEMES_VIEW]: 'View Reward Schemes',
    [PERMISSIONS.REWARDS_SCHEMES_DESIGN]: 'Design Reward Schemes',
    [PERMISSIONS.REWARDS_SCHEMES_MANAGE]: 'Manage Reward Schemes',
    [PERMISSIONS.REWARDS_LOOKUPS_VIEW]: 'View Reward Lookups',
    [PERMISSIONS.REWARDS_LOOKUPS_MANAGE]: 'Manage Reward Lookups',
    [PERMISSIONS.REWARDS_COINS_VIEW]: 'View Coins Balance',
    [PERMISSIONS.REWARDS_COINS_ADJUST]: 'Adjust Coins Balance',
    [PERMISSIONS.REWARDS_BADGES_MANAGE]: 'Manage Badges',
    [PERMISSIONS.REWARDS_MILESTONES_MANAGE]: 'Manage Milestones',

    // Open Banking
    [PERMISSIONS.OPENBANKING_CONFIG_VIEW]: 'View Open Banking Config',
    [PERMISSIONS.OPENBANKING_CONFIG_MANAGE]: 'Manage Open Banking Config',
    [PERMISSIONS.OPENBANKING_CONNECTIONS_VIEW]: 'View Open Banking Connections',
    [PERMISSIONS.OPENBANKING_ALERTS_VIEW]: 'View Open Banking Alerts',
    [PERMISSIONS.OPENBANKING_ALERTS_MANAGE]: 'Manage Open Banking Alerts',
    [PERMISSIONS.OPENBANKING_STATS_VIEW]: 'View Open Banking Stats',
    [PERMISSIONS.OPENBANKING_CONSENTS_VIEW]: 'View Open Banking Consents',

    // Products
    [PERMISSIONS.PRODUCTS_DEFINITIONS_VIEW]: 'View Product Definitions',
    [PERMISSIONS.PRODUCTS_DEFINITIONS_MANAGE]: 'Manage Product Definitions',
    [PERMISSIONS.PRODUCTS_VERSIONS_VIEW]: 'View Product Versions',
    [PERMISSIONS.PRODUCTS_VERSIONS_MANAGE]: 'Manage Product Versions',
    [PERMISSIONS.PRODUCTS_FEES_MANAGE]: 'Manage Product Fees',
    [PERMISSIONS.PRODUCTS_INTEREST_MANAGE]: 'Manage Product Interest',
    [PERMISSIONS.PRODUCTS_ACTIVATE]: 'Activate Products',
    [PERMISSIONS.PRODUCTS_DEACTIVATE]: 'Deactivate Products',

    // Limits
    [PERMISSIONS.LIMITS_VIEW]: 'View Limits',
    [PERMISSIONS.LIMITS_MANAGE]: 'Manage Limits',
    [PERMISSIONS.LIMITS_OVERRIDE]: 'Override Limits',

    // KYB
    [PERMISSIONS.KYB_ENTITIES_VIEW]: 'View KYB Entities',
    [PERMISSIONS.KYB_ENTITIES_MANAGE]: 'Manage KYB Entities',
    [PERMISSIONS.KYB_REVIEWS_VIEW]: 'View KYB Reviews',
    [PERMISSIONS.KYB_REVIEWS_MANAGE]: 'Manage KYB Reviews',
    [PERMISSIONS.KYB_DOCUMENTS_VIEW]: 'View KYB Documents',
    [PERMISSIONS.KYB_DOCUMENTS_MANAGE]: 'Manage KYB Documents',
    [PERMISSIONS.KYB_SCREENING_VIEW]: 'View KYB Screening',
    [PERMISSIONS.KYB_SCREENING_MANAGE]: 'Manage KYB Screening',
    [PERMISSIONS.KYB_CONFIG_VIEW]: 'View KYB Config',
    [PERMISSIONS.KYB_CONFIG_MANAGE]: 'Manage KYB Config',

    // Regulatory
    [PERMISSIONS.REGULATORY_REPORTS_VIEW]: 'View Regulatory Reports',
    [PERMISSIONS.REGULATORY_REPORTS_MANAGE]: 'Manage Regulatory Reports',
    [PERMISSIONS.REGULATORY_SUBMISSIONS_VIEW]: 'View Regulatory Submissions',
    [PERMISSIONS.REGULATORY_SUBMISSIONS_MANAGE]: 'Manage Regulatory Submissions',
    [PERMISSIONS.REGULATORY_CAPITAL_VIEW]: 'View Capital Requirements',
    [PERMISSIONS.REGULATORY_CAPITAL_MANAGE]: 'Manage Capital Requirements',
    [PERMISSIONS.REGULATORY_CONFIG_MANAGE]: 'Manage Regulatory Config',

    // Config
    [PERMISSIONS.CONFIG_VIEW]: 'View Configuration',
    [PERMISSIONS.CONFIG_MANAGE]: 'Manage Configuration',
    [PERMISSIONS.CONFIG_SEED]: 'Seed Configuration',
    [PERMISSIONS.CONFIG_EXPORT]: 'Export Configuration',
    [PERMISSIONS.CONFIG_IMPORT]: 'Import Configuration',

    // Deposits
    [PERMISSIONS.DEPOSITS_RATES_VIEW]: 'View Deposit Rates',
    [PERMISSIONS.DEPOSITS_RATES_MANAGE]: 'Manage Deposit Rates',
    [PERMISSIONS.DEPOSITS_PRODUCTS_MANAGE]: 'Manage Deposit Products',
    [PERMISSIONS.DEPOSITS_ACCOUNTS_MANAGE]: 'Manage Deposit Accounts',
    [PERMISSIONS.DEPOSITS_MATURITY_MANAGE]: 'Manage Deposit Maturity',

    // Insurance
    [PERMISSIONS.INSURANCE_POLICIES_VIEW]: 'View Insurance Policies',
    [PERMISSIONS.INSURANCE_POLICIES_MANAGE]: 'Manage Insurance Policies',
    [PERMISSIONS.INSURANCE_CLAIMS_VIEW]: 'View Insurance Claims',
    [PERMISSIONS.INSURANCE_CLAIMS_MANAGE]: 'Manage Insurance Claims',
    [PERMISSIONS.INSURANCE_PRODUCTS_MANAGE]: 'Manage Insurance Products',

    // Standing Orders
    [PERMISSIONS.STANDING_ORDERS_VIEW]: 'View Standing Orders',
    [PERMISSIONS.STANDING_ORDERS_MANAGE]: 'Manage Standing Orders',
    [PERMISSIONS.STANDING_ORDERS_ADMIN]: 'Administer Standing Orders',

    // Notifications
    [PERMISSIONS.NOTIFICATIONS_TEMPLATES_VIEW]: 'View Notification Templates',
    [PERMISSIONS.NOTIFICATIONS_TEMPLATES_MANAGE]: 'Manage Notification Templates',
    [PERMISSIONS.NOTIFICATIONS_CHANNELS_MANAGE]: 'Manage Notification Channels',
    [PERMISSIONS.NOTIFICATIONS_HISTORY_VIEW]: 'View Notification History',
};

// ─── Permission descriptions ─────────────────────────────────────────────────

export const PERMISSION_DESCRIPTIONS: Record<PermissionName, string> = {
    // Customers
    [PERMISSIONS.CUSTOMERS_VIEW]: 'View customer profiles and information',
    [PERMISSIONS.CUSTOMERS_CREATE]: 'Create new customer accounts',
    [PERMISSIONS.CUSTOMERS_EDIT]: 'Modify existing customer details',
    [PERMISSIONS.CUSTOMERS_DELETE]: 'Delete customer accounts',
    [PERMISSIONS.CUSTOMERS_EXPORT]: 'Export customer data to files',

    // Accounts
    [PERMISSIONS.ACCOUNTS_VIEW]: 'View account information and balances',
    [PERMISSIONS.ACCOUNTS_CREATE]: 'Open new accounts for customers',
    [PERMISSIONS.ACCOUNTS_EDIT]: 'Modify account settings and details',
    [PERMISSIONS.ACCOUNTS_CLOSE]: 'Close customer accounts',
    [PERMISSIONS.ACCOUNTS_FREEZE]: 'Freeze or unfreeze accounts',

    // Transactions
    [PERMISSIONS.TRANSACTIONS_VIEW]: 'View transaction history and details',
    [PERMISSIONS.TRANSACTIONS_CREATE]: 'Initiate new transactions',
    [PERMISSIONS.TRANSACTIONS_REVERSE]: 'Reverse completed transactions',
    [PERMISSIONS.TRANSACTIONS_APPROVE]: 'Approve pending transactions',
    [PERMISSIONS.TRANSACTIONS_EXPORT]: 'Export transaction data to files',

    // Cards
    [PERMISSIONS.CARDS_VIEW]: 'View card information',
    [PERMISSIONS.CARDS_CREATE]: 'Issue new cards',
    [PERMISSIONS.CARDS_MANAGE]: 'Manage card settings and limits',
    [PERMISSIONS.CARDS_BLOCK]: 'Block or unblock cards',

    // Loans
    [PERMISSIONS.LOANS_VIEW]: 'View loan information',
    [PERMISSIONS.LOANS_CREATE]: 'Create loan applications',
    [PERMISSIONS.LOANS_APPROVE]: 'Approve or reject loans',
    [PERMISSIONS.LOANS_MANAGE]: 'Manage loan terms and restructuring',
    [PERMISSIONS.LOANS_DISBURSE]: 'Disburse approved loans to customer accounts',

    // Savings
    [PERMISSIONS.SAVINGS_VIEW]: 'View savings and deposit products',
    [PERMISSIONS.SAVINGS_CREATE]: 'Create savings accounts',
    [PERMISSIONS.SAVINGS_MANAGE]: 'Manage savings product configuration',

    // Users
    [PERMISSIONS.USERS_VIEW]: 'View user profiles and access levels',
    [PERMISSIONS.USERS_CREATE]: 'Create new system users',
    [PERMISSIONS.USERS_EDIT]: 'Modify user details and settings',
    [PERMISSIONS.USERS_DELETE]: 'Remove users from the system',
    [PERMISSIONS.USERS_ASSIGN_ROLES]: 'Assign roles and manage user permissions',

    // Reports
    [PERMISSIONS.REPORTS_VIEW]: 'Access and view system reports',
    [PERMISSIONS.REPORTS_CREATE]: 'Create custom report templates',
    [PERMISSIONS.REPORTS_EXPORT]: 'Export reports to various formats',
    [PERMISSIONS.REPORTS_SCHEDULE]: 'Set up automatic report generation',

    // Settings
    [PERMISSIONS.SETTINGS_VIEW]: 'View system configuration and settings',
    [PERMISSIONS.SETTINGS_EDIT]: 'Modify system settings and configuration',
    [PERMISSIONS.SETTINGS_SECURITY]: 'Manage security policies and settings',

    // Audit
    [PERMISSIONS.AUDIT_VIEW]: 'View system audit trail and logs',
    [PERMISSIONS.AUDIT_EXPORT]: 'Export audit logs for compliance',

    // General Ledger
    [PERMISSIONS.GL_VIEW]: 'View chart of accounts, journal entries, and trial balance',
    [PERMISSIONS.GL_MANAGE]: 'Create and edit GL accounts and manage periods',
    [PERMISSIONS.GL_JOURNALS_CREATE]: 'Create new journal entries',
    [PERMISSIONS.GL_JOURNALS_APPROVE]: 'Approve or reject pending journal entries',

    // Branch Banking
    [PERMISSIONS.BRANCH_BANKING_ACCESS]: 'Access branch banking teller operations',
    [PERMISSIONS.BRANCH_TRANSACTIONS_CREATE]: 'Process deposits, withdrawals, and transfers at branch',
    [PERMISSIONS.BRANCH_ACCOUNTS_OPEN]: 'Open new accounts for walk-in customers',
    [PERMISSIONS.BRANCH_CASH_MANAGE]: 'Manage cash drawer, reconciliation, and vault transfers',

    // Compliance
    [PERMISSIONS.COMPLIANCE_VIEW]: 'View compliance dashboard, alerts, and regulatory returns',
    [PERMISSIONS.COMPLIANCE_MANAGE]: 'Manage AML alerts, investigate and escalate cases',
    [PERMISSIONS.COMPLIANCE_SAR_FILE]: 'Create and submit Suspicious Activity Reports to NCA',
    [PERMISSIONS.COMPLIANCE_SANCTIONS_SCREEN]: 'Run sanctions, PEP, and adverse media screening',

    // Payment Operations
    [PERMISSIONS.PAYMENTS_VIEW]: 'View payment transactions and standing orders',
    [PERMISSIONS.PAYMENTS_MANAGE]: 'Retry, escalate, and manage failed payments and batches',
    [PERMISSIONS.PAYMENTS_RECONCILE]: 'Run and review payment reconciliation',
    [PERMISSIONS.PAYMENTS_SCHEME_CONFIG]: 'Configure payment scheme routing rules',

    // Platform
    [PERMISSIONS.PLATFORM_INSTITUTIONS_CREATE]: 'Create new financial institutions',
    [PERMISSIONS.PLATFORM_INSTITUTIONS_MANAGE]: 'Manage all institutions on the platform',
    [PERMISSIONS.PLATFORM_ROUTES_MANAGE]: 'Create, edit, and delete gateway routes',
    [PERMISSIONS.PLATFORM_SUPPORT_ACCESS]: 'Access cross-institution support tools',
    [PERMISSIONS.PLATFORM_SYSTEM_CONFIG]: 'Modify system-level configuration',
    [PERMISSIONS.PLATFORM_HEALTH_VIEW]: 'View platform service health, infrastructure status, and diagnostics',
    [PERMISSIONS.PLATFORM_METERING_VIEW]: 'View institution usage metrics, metering data, and invoices',
    [PERMISSIONS.PLATFORM_BILLING_MANAGE]: 'Create, edit, and manage billing plans and pricing tiers',

    // Mortgages
    [PERMISSIONS.MORTGAGE_VIEW]: 'View mortgage pipeline and applications',
    [PERMISSIONS.MORTGAGE_MANAGE]: 'Manage mortgage applications and pipeline',
    [PERMISSIONS.MORTGAGE_APPROVE]: 'Approve or decline mortgage applications',

    // Neural / AI
    [PERMISSIONS.NEURAL_PERSONALITY_VIEW]: 'View AI personality configuration and tone settings',
    [PERMISSIONS.NEURAL_PERSONALITY_MANAGE]: 'Edit and update AI personality and tone parameters',
    [PERMISSIONS.NEURAL_RULES_VIEW]: 'View neural engine business rules and triggers',
    [PERMISSIONS.NEURAL_RULES_MANAGE]: 'Create, edit, and delete neural engine rules',
    [PERMISSIONS.NEURAL_FEEDBACK_VIEW]: 'View AI feedback, ratings, and quality metrics',
    [PERMISSIONS.NEURAL_SESSIONS_VIEW]: 'View AI conversation sessions and transcripts',
    [PERMISSIONS.NEURAL_SESSIONS_MANAGE]: 'Manage AI sessions, flag, and annotate conversations',
    [PERMISSIONS.NEURAL_AGENTS_CONFIG]: 'Configure AI agent behaviour, routing, and capabilities',
    [PERMISSIONS.NEURAL_MODELS_MANAGE]: 'Manage AI model versions, deployments, and rollbacks',

    // Insights
    [PERMISSIONS.INSIGHTS_RULES_VIEW]: 'View insight engine rules and conditions',
    [PERMISSIONS.INSIGHTS_RULES_DESIGN]: 'Design and draft new insight rules in the rule builder',
    [PERMISSIONS.INSIGHTS_RULES_MANAGE]: 'Activate, deactivate, and manage insight rules',
    [PERMISSIONS.INSIGHTS_SCHEDULES_VIEW]: 'View insight delivery schedules and run history',
    [PERMISSIONS.INSIGHTS_SCHEDULES_MANAGE]: 'Create and manage insight delivery schedules',
    [PERMISSIONS.INSIGHTS_ANALYTICS_VIEW]: 'View insight analytics, engagement, and performance',
    [PERMISSIONS.INSTITUTION_INSIGHTS_VIEW]: 'View institution insight configuration and results',
    [PERMISSIONS.INSTITUTION_INSIGHTS_MANAGE]: 'Manage institution insight rules',
    [PERMISSIONS.INSTITUTION_REWARDS_VIEW]: 'View institution reward configuration and activity',
    [PERMISSIONS.INSTITUTION_REWARDS_MANAGE]: 'Manage institution rewards',
    [PERMISSIONS.INSTITUTION_JOURNEYS_VIEW]: 'View institution customer journeys',
    [PERMISSIONS.INSTITUTION_JOURNEYS_MANAGE]: 'Manage institution journey templates and journeys',
    [PERMISSIONS.INSTITUTION_NOTIFICATIONS_VIEW]: 'View institution notification history and templates',
    [PERMISSIONS.INSTITUTION_NOTIFICATIONS_MANAGE]: 'Manage institution notification templates and policy',

    // Profiles
    [PERMISSIONS.PROFILES_TRAITS_VIEW]: 'View customer profile traits and computed attributes',
    [PERMISSIONS.PROFILES_TRAITS_DESIGN]: 'Design and define new profile trait definitions',
    [PERMISSIONS.PROFILES_TRAITS_MANAGE]: 'Manage profile traits, weights, and computation rules',
    [PERMISSIONS.PROFILES_OVERRIDES_VIEW]: 'View manual profile overrides and adjustments',
    [PERMISSIONS.PROFILES_OVERRIDES_MANAGE]: 'Create and manage profile trait overrides',
    [PERMISSIONS.INSTITUTION_PROFILE_TRAITS_VIEW]: 'View institution profile traits',
    [PERMISSIONS.INSTITUTION_PROFILE_TRAITS_MANAGE]: 'Manage institution profile traits',
    [PERMISSIONS.INSTITUTION_MONI_FEEDBACK_VIEW]: 'View Moni feedback for the institution',

    // Journeys
    [PERMISSIONS.JOURNEYS_TEMPLATES_VIEW]: 'View customer journey templates and flows',
    [PERMISSIONS.JOURNEYS_TEMPLATES_MANAGE]: 'Create, edit, and manage journey templates',
    [PERMISSIONS.JOURNEYS_TEMPLATES_PUBLISH]: 'Publish journey templates to production',
    [PERMISSIONS.JOURNEYS_VERSIONS_VIEW]: 'View journey template version history',

    // Providers
    [PERMISSIONS.PROVIDERS_CATALOG_VIEW]: 'View provider catalog and available integrations',
    [PERMISSIONS.PROVIDERS_CATALOG_MANAGE]: 'Add, edit, and remove providers from the catalog',
    [PERMISSIONS.PROVIDERS_CONTRACTS_VIEW]: 'View provider contracts and SLA agreements',
    [PERMISSIONS.PROVIDERS_CONTRACTS_MANAGE]: 'Manage provider contracts, terms, and renewals',
    [PERMISSIONS.PROVIDERS_HEALTH_VIEW]: 'View provider health status and uptime metrics',
    [PERMISSIONS.PROVIDERS_SWAP]: 'Swap active provider for a service without downtime',
    [PERMISSIONS.PROVIDERS_METERING_VIEW]: 'View provider API usage and metering data',

    // Fees
    [PERMISSIONS.FEES_DEFINITIONS_VIEW]: 'View fee definitions and pricing structures',
    [PERMISSIONS.FEES_DEFINITIONS_MANAGE]: 'Create, edit, and manage fee definitions',
    [PERMISSIONS.FEES_SCHEDULES_VIEW]: 'View fee schedules and effective date ranges',
    [PERMISSIONS.FEES_SCHEDULES_MANAGE]: 'Create and manage fee application schedules',
    [PERMISSIONS.FEES_SIMULATE]: 'Run fee simulations to preview customer impact',

    // Rewards
    [PERMISSIONS.REWARDS_CONFIG_VIEW]: 'View rewards programme configuration',
    [PERMISSIONS.REWARDS_CONFIG_MANAGE]: 'Manage rewards programme global settings',
    [PERMISSIONS.REWARDS_SCHEMES_VIEW]: 'View reward schemes and earning rules',
    [PERMISSIONS.REWARDS_SCHEMES_DESIGN]: 'Design new reward schemes in the scheme builder',
    [PERMISSIONS.REWARDS_SCHEMES_MANAGE]: 'Activate, deactivate, and manage reward schemes',
    [PERMISSIONS.REWARDS_LOOKUPS_VIEW]: 'View customer reward balances and history',
    [PERMISSIONS.REWARDS_LOOKUPS_MANAGE]: 'Manage reward lookups and reference data',
    [PERMISSIONS.REWARDS_COINS_VIEW]: 'View customer coin balances and transactions',
    [PERMISSIONS.REWARDS_COINS_ADJUST]: 'Manually adjust customer coin balances',
    [PERMISSIONS.REWARDS_BADGES_MANAGE]: 'Create, edit, and manage achievement badges',
    [PERMISSIONS.REWARDS_MILESTONES_MANAGE]: 'Create, edit, and manage reward milestones',

    // Open Banking
    [PERMISSIONS.OPENBANKING_CONFIG_VIEW]: 'View open banking integration configuration',
    [PERMISSIONS.OPENBANKING_CONFIG_MANAGE]: 'Manage open banking API keys and endpoints',
    [PERMISSIONS.OPENBANKING_CONNECTIONS_VIEW]: 'View active open banking connections',
    [PERMISSIONS.OPENBANKING_ALERTS_VIEW]: 'View open banking alerts and notifications',
    [PERMISSIONS.OPENBANKING_ALERTS_MANAGE]: 'Manage open banking alert rules and thresholds',
    [PERMISSIONS.OPENBANKING_STATS_VIEW]: 'View open banking usage statistics and analytics',
    [PERMISSIONS.OPENBANKING_CONSENTS_VIEW]: 'View customer open banking consent records',

    // Products
    [PERMISSIONS.PRODUCTS_DEFINITIONS_VIEW]: 'View product definitions and configurations',
    [PERMISSIONS.PRODUCTS_DEFINITIONS_MANAGE]: 'Create and edit product definitions',
    [PERMISSIONS.PRODUCTS_VERSIONS_VIEW]: 'View product version history and change log',
    [PERMISSIONS.PRODUCTS_VERSIONS_MANAGE]: 'Manage product versions and promote drafts',
    [PERMISSIONS.PRODUCTS_FEES_MANAGE]: 'Attach and manage fees on product definitions',
    [PERMISSIONS.PRODUCTS_INTEREST_MANAGE]: 'Configure interest rates and tiers on products',
    [PERMISSIONS.PRODUCTS_ACTIVATE]: 'Activate products for customer availability',
    [PERMISSIONS.PRODUCTS_DEACTIVATE]: 'Deactivate products and remove from availability',

    // Limits
    [PERMISSIONS.LIMITS_VIEW]: 'View transaction and account limits',
    [PERMISSIONS.LIMITS_MANAGE]: 'Create and manage limit definitions',
    [PERMISSIONS.LIMITS_OVERRIDE]: 'Override limits for specific accounts or customers',

    // KYB
    [PERMISSIONS.KYB_ENTITIES_VIEW]: 'View KYB business entities and details',
    [PERMISSIONS.KYB_ENTITIES_MANAGE]: 'Create and manage KYB business entities',
    [PERMISSIONS.KYB_REVIEWS_VIEW]: 'View KYB review queue and status',
    [PERMISSIONS.KYB_REVIEWS_MANAGE]: 'Approve, reject, and manage KYB reviews',
    [PERMISSIONS.KYB_DOCUMENTS_VIEW]: 'View KYB uploaded documents and evidence',
    [PERMISSIONS.KYB_DOCUMENTS_MANAGE]: 'Upload, verify, and manage KYB documents',
    [PERMISSIONS.KYB_SCREENING_VIEW]: 'View KYB screening results and risk scores',
    [PERMISSIONS.KYB_SCREENING_MANAGE]: 'Run and manage KYB screening checks',
    [PERMISSIONS.KYB_CONFIG_VIEW]: 'View KYB configuration and workflow rules',
    [PERMISSIONS.KYB_CONFIG_MANAGE]: 'Manage KYB configuration and verification workflows',

    // Regulatory
    [PERMISSIONS.REGULATORY_REPORTS_VIEW]: 'View regulatory reports and filings',
    [PERMISSIONS.REGULATORY_REPORTS_MANAGE]: 'Generate and manage regulatory reports',
    [PERMISSIONS.REGULATORY_SUBMISSIONS_VIEW]: 'View regulatory submission history',
    [PERMISSIONS.REGULATORY_SUBMISSIONS_MANAGE]: 'Submit and manage regulatory filings',
    [PERMISSIONS.REGULATORY_CAPITAL_VIEW]: 'View capital adequacy ratios and reports',
    [PERMISSIONS.REGULATORY_CAPITAL_MANAGE]: 'Manage capital requirement calculations',
    [PERMISSIONS.REGULATORY_CONFIG_MANAGE]: 'Manage regulatory reporting configuration',

    // Config
    [PERMISSIONS.CONFIG_VIEW]: 'View system configuration parameters',
    [PERMISSIONS.CONFIG_MANAGE]: 'Edit system configuration parameters',
    [PERMISSIONS.CONFIG_SEED]: 'Seed initial configuration and reference data',
    [PERMISSIONS.CONFIG_EXPORT]: 'Export configuration to file',
    [PERMISSIONS.CONFIG_IMPORT]: 'Import configuration from file',

    // Deposits
    [PERMISSIONS.DEPOSITS_RATES_VIEW]: 'View deposit interest rates and tiers',
    [PERMISSIONS.DEPOSITS_RATES_MANAGE]: 'Manage deposit interest rates and tiers',
    [PERMISSIONS.DEPOSITS_PRODUCTS_MANAGE]: 'Manage deposit product definitions',
    [PERMISSIONS.DEPOSITS_ACCOUNTS_MANAGE]: 'Manage deposit accounts and operations',
    [PERMISSIONS.DEPOSITS_MATURITY_MANAGE]: 'Manage deposit maturity and rollover settings',

    // Insurance
    [PERMISSIONS.INSURANCE_POLICIES_VIEW]: 'View insurance policies and coverage',
    [PERMISSIONS.INSURANCE_POLICIES_MANAGE]: 'Manage insurance policies and endorsements',
    [PERMISSIONS.INSURANCE_CLAIMS_VIEW]: 'View insurance claims and adjudication status',
    [PERMISSIONS.INSURANCE_CLAIMS_MANAGE]: 'Process and manage insurance claims',
    [PERMISSIONS.INSURANCE_PRODUCTS_MANAGE]: 'Manage insurance product definitions',

    // Standing Orders
    [PERMISSIONS.STANDING_ORDERS_VIEW]: 'View standing orders and recurring payments',
    [PERMISSIONS.STANDING_ORDERS_MANAGE]: 'Create, edit, and cancel standing orders',
    [PERMISSIONS.STANDING_ORDERS_ADMIN]: 'Administrative control over all standing orders',

    // Notifications
    [PERMISSIONS.NOTIFICATIONS_TEMPLATES_VIEW]: 'View notification templates and content',
    [PERMISSIONS.NOTIFICATIONS_TEMPLATES_MANAGE]: 'Create and manage notification templates',
    [PERMISSIONS.NOTIFICATIONS_CHANNELS_MANAGE]: 'Configure notification delivery channels',
    [PERMISSIONS.NOTIFICATIONS_HISTORY_VIEW]: 'View notification delivery history and logs',
};

// ─── Permission categories ───────────────────────────────────────────────────

export interface PermissionCategory {
    name: string;
    label: string;
    icon: string;
    description: string;
    permissions: PermissionName[];
}

export const PERMISSION_CATEGORIES: PermissionCategory[] = [
    {
        name: 'customers',
        label: 'Customer Management',
        icon: 'TeamOutlined',
        description: 'Manage customer accounts and information',
        permissions: [
            PERMISSIONS.CUSTOMERS_VIEW,
            PERMISSIONS.CUSTOMERS_CREATE,
            PERMISSIONS.CUSTOMERS_EDIT,
            PERMISSIONS.CUSTOMERS_DELETE,
            PERMISSIONS.CUSTOMERS_EXPORT,
        ],
    },
    {
        name: 'accounts',
        label: 'Account Management',
        icon: 'BankOutlined',
        description: 'Manage customer accounts and balances',
        permissions: [
            PERMISSIONS.ACCOUNTS_VIEW,
            PERMISSIONS.ACCOUNTS_CREATE,
            PERMISSIONS.ACCOUNTS_EDIT,
            PERMISSIONS.ACCOUNTS_CLOSE,
            PERMISSIONS.ACCOUNTS_FREEZE,
        ],
    },
    {
        name: 'transactions',
        label: 'Transaction Management',
        icon: 'SwapOutlined',
        description: 'Handle transaction processing and approvals',
        permissions: [
            PERMISSIONS.TRANSACTIONS_VIEW,
            PERMISSIONS.TRANSACTIONS_CREATE,
            PERMISSIONS.TRANSACTIONS_REVERSE,
            PERMISSIONS.TRANSACTIONS_APPROVE,
            PERMISSIONS.TRANSACTIONS_EXPORT,
        ],
    },
    {
        name: 'cards',
        label: 'Card Management',
        icon: 'CreditCardOutlined',
        description: 'Issue and manage payment cards',
        permissions: [
            PERMISSIONS.CARDS_VIEW,
            PERMISSIONS.CARDS_CREATE,
            PERMISSIONS.CARDS_MANAGE,
            PERMISSIONS.CARDS_BLOCK,
        ],
    },
    {
        name: 'loans',
        label: 'Loan Management',
        icon: 'DollarOutlined',
        description: 'Manage loan applications and terms',
        permissions: [
            PERMISSIONS.LOANS_VIEW,
            PERMISSIONS.LOANS_CREATE,
            PERMISSIONS.LOANS_APPROVE,
            PERMISSIONS.LOANS_MANAGE,
            PERMISSIONS.LOANS_DISBURSE,
        ],
    },
    {
        name: 'savings',
        label: 'Savings & Deposits',
        icon: 'WalletOutlined',
        description: 'Manage savings and deposit products',
        permissions: [
            PERMISSIONS.SAVINGS_VIEW,
            PERMISSIONS.SAVINGS_CREATE,
            PERMISSIONS.SAVINGS_MANAGE,
        ],
    },
    {
        name: 'users',
        label: 'User Management',
        icon: 'UserOutlined',
        description: 'Manage system users and access control',
        permissions: [
            PERMISSIONS.USERS_VIEW,
            PERMISSIONS.USERS_CREATE,
            PERMISSIONS.USERS_EDIT,
            PERMISSIONS.USERS_DELETE,
            PERMISSIONS.USERS_ASSIGN_ROLES,
        ],
    },
    {
        name: 'reports',
        label: 'Reporting',
        icon: 'BarChartOutlined',
        description: 'Generate and manage reports',
        permissions: [
            PERMISSIONS.REPORTS_VIEW,
            PERMISSIONS.REPORTS_CREATE,
            PERMISSIONS.REPORTS_EXPORT,
            PERMISSIONS.REPORTS_SCHEDULE,
        ],
    },
    {
        name: 'settings',
        label: 'Settings',
        icon: 'SettingOutlined',
        description: 'Configure system settings',
        permissions: [
            PERMISSIONS.SETTINGS_VIEW,
            PERMISSIONS.SETTINGS_EDIT,
            PERMISSIONS.SETTINGS_SECURITY,
        ],
    },
    {
        name: 'audit',
        label: 'Audit & Compliance',
        icon: 'AuditOutlined',
        description: 'View and export audit trails',
        permissions: [
            PERMISSIONS.AUDIT_VIEW,
            PERMISSIONS.AUDIT_EXPORT,
        ],
    },
    {
        name: 'general-ledger',
        label: 'General Ledger',
        icon: 'BookOutlined',
        description: 'View and manage the general ledger',
        permissions: [
            PERMISSIONS.GL_VIEW,
            PERMISSIONS.GL_MANAGE,
            PERMISSIONS.GL_JOURNALS_CREATE,
            PERMISSIONS.GL_JOURNALS_APPROVE,
        ],
    },
    {
        name: 'branch',
        label: 'Branch Banking',
        icon: 'BankOutlined',
        description: 'Branch teller operations and cash management',
        permissions: [
            PERMISSIONS.BRANCH_BANKING_ACCESS,
            PERMISSIONS.BRANCH_TRANSACTIONS_CREATE,
            PERMISSIONS.BRANCH_ACCOUNTS_OPEN,
            PERMISSIONS.BRANCH_CASH_MANAGE,
        ],
    },
    {
        name: 'compliance',
        label: 'Compliance & AML',
        icon: 'SafetyCertificateOutlined',
        description: 'AML monitoring, SAR filing, sanctions screening, and regulatory returns',
        permissions: [
            PERMISSIONS.COMPLIANCE_VIEW,
            PERMISSIONS.COMPLIANCE_MANAGE,
            PERMISSIONS.COMPLIANCE_SAR_FILE,
            PERMISSIONS.COMPLIANCE_SANCTIONS_SCREEN,
        ],
    },
    {
        name: 'payment-ops',
        label: 'Payment Operations',
        icon: 'BankOutlined',
        description: 'Payment processing, reconciliation, and scheme routing',
        permissions: [
            PERMISSIONS.PAYMENTS_VIEW,
            PERMISSIONS.PAYMENTS_MANAGE,
            PERMISSIONS.PAYMENTS_RECONCILE,
            PERMISSIONS.PAYMENTS_SCHEME_CONFIG,
        ],
    },
    {
        name: 'platform',
        label: 'Platform Administration',
        icon: 'CloudServerOutlined',
        description: 'OneGroup platform-level operations',
        permissions: [
            PERMISSIONS.PLATFORM_INSTITUTIONS_CREATE,
            PERMISSIONS.PLATFORM_INSTITUTIONS_MANAGE,
            PERMISSIONS.PLATFORM_ROUTES_MANAGE,
            PERMISSIONS.PLATFORM_SUPPORT_ACCESS,
            PERMISSIONS.PLATFORM_SYSTEM_CONFIG,
            PERMISSIONS.PLATFORM_HEALTH_VIEW,
            PERMISSIONS.PLATFORM_METERING_VIEW,
            PERMISSIONS.PLATFORM_BILLING_MANAGE,
        ],
    },
    {
        name: 'mortgages',
        label: 'Mortgages',
        icon: 'HomeOutlined',
        description: 'Mortgage pipeline and application management',
        permissions: [
            PERMISSIONS.MORTGAGE_VIEW,
            PERMISSIONS.MORTGAGE_MANAGE,
            PERMISSIONS.MORTGAGE_APPROVE,
        ],
    },
    {
        name: 'neural',
        label: 'Neural / AI',
        icon: 'RobotOutlined',
        description: 'AI personality, rules, sessions, and model management',
        permissions: [
            PERMISSIONS.NEURAL_PERSONALITY_VIEW,
            PERMISSIONS.NEURAL_PERSONALITY_MANAGE,
            PERMISSIONS.NEURAL_RULES_VIEW,
            PERMISSIONS.NEURAL_RULES_MANAGE,
            PERMISSIONS.NEURAL_FEEDBACK_VIEW,
            PERMISSIONS.NEURAL_SESSIONS_VIEW,
            PERMISSIONS.NEURAL_SESSIONS_MANAGE,
            PERMISSIONS.NEURAL_AGENTS_CONFIG,
            PERMISSIONS.NEURAL_MODELS_MANAGE,
        ],
    },
    {
        name: 'insights',
        label: 'Insights',
        icon: 'BulbOutlined',
        description: 'Insight rules, schedules, and analytics',
        permissions: [
            PERMISSIONS.INSTITUTION_INSIGHTS_VIEW,
            PERMISSIONS.INSTITUTION_INSIGHTS_MANAGE,
        ],
    },
    {
        name: 'profiles',
        label: 'Profiles',
        icon: 'IdcardOutlined',
        description: 'Customer profile traits and overrides',
        permissions: [
            PERMISSIONS.INSTITUTION_PROFILE_TRAITS_VIEW,
            PERMISSIONS.INSTITUTION_PROFILE_TRAITS_MANAGE,
        ],
    },
    {
        name: 'moni-feedback',
        label: 'Moni Feedback',
        icon: 'RobotOutlined',
        description: 'Institution-scoped Moni feedback and issue review',
        permissions: [
            PERMISSIONS.INSTITUTION_MONI_FEEDBACK_VIEW,
        ],
    },
    {
        name: 'journeys',
        label: 'Journeys',
        icon: 'NodeIndexOutlined',
        description: 'Customer journey templates and versioning',
        permissions: [
            PERMISSIONS.INSTITUTION_JOURNEYS_VIEW,
            PERMISSIONS.INSTITUTION_JOURNEYS_MANAGE,
        ],
    },
    {
        name: 'providers',
        label: 'Providers',
        icon: 'ApiOutlined',
        description: 'Provider catalog, contracts, health, and metering',
        permissions: [
            PERMISSIONS.PROVIDERS_CATALOG_VIEW,
            PERMISSIONS.PROVIDERS_CATALOG_MANAGE,
            PERMISSIONS.PROVIDERS_CONTRACTS_VIEW,
            PERMISSIONS.PROVIDERS_CONTRACTS_MANAGE,
            PERMISSIONS.PROVIDERS_HEALTH_VIEW,
            PERMISSIONS.PROVIDERS_SWAP,
            PERMISSIONS.PROVIDERS_METERING_VIEW,
        ],
    },
    {
        name: 'fees',
        label: 'Fees',
        icon: 'CalculatorOutlined',
        description: 'Fee definitions, schedules, and simulation',
        permissions: [
            PERMISSIONS.FEES_DEFINITIONS_VIEW,
            PERMISSIONS.FEES_DEFINITIONS_MANAGE,
            PERMISSIONS.FEES_SCHEDULES_VIEW,
            PERMISSIONS.FEES_SCHEDULES_MANAGE,
            PERMISSIONS.FEES_SIMULATE,
        ],
    },
    {
        name: 'rewards',
        label: 'Rewards',
        icon: 'GiftOutlined',
        description: 'Rewards programme, schemes, coins, badges, and milestones',
        permissions: [
            PERMISSIONS.INSTITUTION_REWARDS_VIEW,
            PERMISSIONS.INSTITUTION_REWARDS_MANAGE,
        ],
    },
    {
        name: 'open-banking',
        label: 'Open Banking',
        icon: 'LinkOutlined',
        description: 'Open banking configuration, connections, and consents',
        permissions: [
            PERMISSIONS.OPENBANKING_CONFIG_VIEW,
            PERMISSIONS.OPENBANKING_CONFIG_MANAGE,
            PERMISSIONS.OPENBANKING_CONNECTIONS_VIEW,
            PERMISSIONS.OPENBANKING_ALERTS_VIEW,
            PERMISSIONS.OPENBANKING_ALERTS_MANAGE,
            PERMISSIONS.OPENBANKING_STATS_VIEW,
            PERMISSIONS.OPENBANKING_CONSENTS_VIEW,
        ],
    },
    {
        name: 'products',
        label: 'Products',
        icon: 'AppstoreOutlined',
        description: 'Product definitions, versioning, and lifecycle',
        permissions: [
            PERMISSIONS.PRODUCTS_DEFINITIONS_VIEW,
            PERMISSIONS.PRODUCTS_DEFINITIONS_MANAGE,
            PERMISSIONS.PRODUCTS_VERSIONS_VIEW,
            PERMISSIONS.PRODUCTS_VERSIONS_MANAGE,
            PERMISSIONS.PRODUCTS_FEES_MANAGE,
            PERMISSIONS.PRODUCTS_INTEREST_MANAGE,
            PERMISSIONS.PRODUCTS_ACTIVATE,
            PERMISSIONS.PRODUCTS_DEACTIVATE,
        ],
    },
    {
        name: 'limits',
        label: 'Limits',
        icon: 'DashboardOutlined',
        description: 'Transaction and account limit management',
        permissions: [
            PERMISSIONS.LIMITS_VIEW,
            PERMISSIONS.LIMITS_MANAGE,
            PERMISSIONS.LIMITS_OVERRIDE,
        ],
    },
    {
        name: 'kyb',
        label: 'KYB',
        icon: 'SolutionOutlined',
        description: 'Know Your Business entity verification and screening',
        permissions: [
            PERMISSIONS.KYB_ENTITIES_VIEW,
            PERMISSIONS.KYB_ENTITIES_MANAGE,
            PERMISSIONS.KYB_REVIEWS_VIEW,
            PERMISSIONS.KYB_REVIEWS_MANAGE,
            PERMISSIONS.KYB_DOCUMENTS_VIEW,
            PERMISSIONS.KYB_DOCUMENTS_MANAGE,
            PERMISSIONS.KYB_SCREENING_VIEW,
            PERMISSIONS.KYB_SCREENING_MANAGE,
            PERMISSIONS.KYB_CONFIG_VIEW,
            PERMISSIONS.KYB_CONFIG_MANAGE,
        ],
    },
    {
        name: 'regulatory',
        label: 'Regulatory',
        icon: 'FileProtectOutlined',
        description: 'Regulatory reporting, submissions, and capital requirements',
        permissions: [
            PERMISSIONS.REGULATORY_REPORTS_VIEW,
            PERMISSIONS.REGULATORY_REPORTS_MANAGE,
            PERMISSIONS.REGULATORY_SUBMISSIONS_VIEW,
            PERMISSIONS.REGULATORY_SUBMISSIONS_MANAGE,
            PERMISSIONS.REGULATORY_CAPITAL_VIEW,
            PERMISSIONS.REGULATORY_CAPITAL_MANAGE,
            PERMISSIONS.REGULATORY_CONFIG_MANAGE,
        ],
    },
    {
        name: 'config',
        label: 'Configuration',
        icon: 'ControlOutlined',
        description: 'System configuration, seeding, import, and export',
        permissions: [
            PERMISSIONS.CONFIG_VIEW,
            PERMISSIONS.CONFIG_MANAGE,
            PERMISSIONS.CONFIG_SEED,
            PERMISSIONS.CONFIG_EXPORT,
            PERMISSIONS.CONFIG_IMPORT,
        ],
    },
    {
        name: 'deposits',
        label: 'Deposits',
        icon: 'FundOutlined',
        description: 'Deposit rates, products, accounts, and maturity',
        permissions: [
            PERMISSIONS.DEPOSITS_RATES_VIEW,
            PERMISSIONS.DEPOSITS_RATES_MANAGE,
            PERMISSIONS.DEPOSITS_PRODUCTS_MANAGE,
            PERMISSIONS.DEPOSITS_ACCOUNTS_MANAGE,
            PERMISSIONS.DEPOSITS_MATURITY_MANAGE,
        ],
    },
    {
        name: 'insurance',
        label: 'Insurance',
        icon: 'InsuranceOutlined',
        description: 'Insurance policies, claims, and product management',
        permissions: [
            PERMISSIONS.INSURANCE_POLICIES_VIEW,
            PERMISSIONS.INSURANCE_POLICIES_MANAGE,
            PERMISSIONS.INSURANCE_CLAIMS_VIEW,
            PERMISSIONS.INSURANCE_CLAIMS_MANAGE,
            PERMISSIONS.INSURANCE_PRODUCTS_MANAGE,
        ],
    },
    {
        name: 'standing-orders',
        label: 'Standing Orders',
        icon: 'ScheduleOutlined',
        description: 'Standing orders and recurring payment management',
        permissions: [
            PERMISSIONS.STANDING_ORDERS_VIEW,
            PERMISSIONS.STANDING_ORDERS_MANAGE,
            PERMISSIONS.STANDING_ORDERS_ADMIN,
        ],
    },
    {
        name: 'notifications',
        label: 'Notifications',
        icon: 'BellOutlined',
        description: 'Notification templates, channels, and delivery history',
        permissions: [
            PERMISSIONS.INSTITUTION_NOTIFICATIONS_VIEW,
            PERMISSIONS.INSTITUTION_NOTIFICATIONS_MANAGE,
        ],
    },
];

const INSTITUTION_OVERRIDE_PERMISSION_NAMES = new Set<PermissionName>([
    PERMISSIONS.CUSTOMERS_VIEW,
    PERMISSIONS.CUSTOMERS_CREATE,
    PERMISSIONS.CUSTOMERS_EDIT,
    PERMISSIONS.CUSTOMERS_DELETE,
    PERMISSIONS.CUSTOMERS_EXPORT,
    PERMISSIONS.ACCOUNTS_VIEW,
    PERMISSIONS.ACCOUNTS_CREATE,
    PERMISSIONS.ACCOUNTS_EDIT,
    PERMISSIONS.ACCOUNTS_CLOSE,
    PERMISSIONS.ACCOUNTS_FREEZE,
    PERMISSIONS.TRANSACTIONS_VIEW,
    PERMISSIONS.TRANSACTIONS_CREATE,
    PERMISSIONS.TRANSACTIONS_REVERSE,
    PERMISSIONS.TRANSACTIONS_APPROVE,
    PERMISSIONS.TRANSACTIONS_EXPORT,
    PERMISSIONS.CARDS_VIEW,
    PERMISSIONS.CARDS_CREATE,
    PERMISSIONS.CARDS_MANAGE,
    PERMISSIONS.CARDS_BLOCK,
    PERMISSIONS.LOANS_VIEW,
    PERMISSIONS.LOANS_CREATE,
    PERMISSIONS.LOANS_APPROVE,
    PERMISSIONS.LOANS_MANAGE,
    PERMISSIONS.SAVINGS_VIEW,
    PERMISSIONS.SAVINGS_CREATE,
    PERMISSIONS.SAVINGS_MANAGE,
    PERMISSIONS.USERS_VIEW,
    PERMISSIONS.USERS_CREATE,
    PERMISSIONS.USERS_EDIT,
    PERMISSIONS.USERS_DELETE,
    PERMISSIONS.USERS_ASSIGN_ROLES,
    PERMISSIONS.REPORTS_VIEW,
    PERMISSIONS.REPORTS_CREATE,
    PERMISSIONS.REPORTS_EXPORT,
    PERMISSIONS.REPORTS_SCHEDULE,
    PERMISSIONS.SETTINGS_VIEW,
    PERMISSIONS.SETTINGS_EDIT,
    PERMISSIONS.SETTINGS_SECURITY,
    PERMISSIONS.AUDIT_VIEW,
    PERMISSIONS.AUDIT_EXPORT,
    PERMISSIONS.INSTITUTION_INSIGHTS_VIEW,
    PERMISSIONS.INSTITUTION_INSIGHTS_MANAGE,
    PERMISSIONS.INSTITUTION_REWARDS_VIEW,
    PERMISSIONS.INSTITUTION_REWARDS_MANAGE,
    PERMISSIONS.INSTITUTION_JOURNEYS_VIEW,
    PERMISSIONS.INSTITUTION_JOURNEYS_MANAGE,
    PERMISSIONS.INSTITUTION_NOTIFICATIONS_VIEW,
    PERMISSIONS.INSTITUTION_NOTIFICATIONS_MANAGE,
    PERMISSIONS.INSTITUTION_PROFILE_TRAITS_VIEW,
    PERMISSIONS.INSTITUTION_PROFILE_TRAITS_MANAGE,
    PERMISSIONS.INSTITUTION_MONI_FEEDBACK_VIEW,
]);

export const INSTITUTION_OVERRIDE_PERMISSION_CATEGORIES: PermissionCategory[] =
    PERMISSION_CATEGORIES
        .map(category => ({
            ...category,
            permissions: category.permissions.filter(permission =>
                INSTITUTION_OVERRIDE_PERMISSION_NAMES.has(permission)),
        }))
        .filter(category => category.permissions.length > 0);

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Return every permission name as an array. */
export const getAllPermissions = (): PermissionName[] => {
    return Object.values(PERMISSIONS);
};

/** Return permissions accepted as workspace-specific user overrides. */
export const getInstitutionOverridePermissions = (): PermissionName[] => {
    return Array.from(INSTITUTION_OVERRIDE_PERMISSION_NAMES);
};

/** Return all permissions belonging to a category. */
export const getPermissionsByCategory = (categoryName: string): PermissionName[] => {
    const category = PERMISSION_CATEGORIES.find(c => c.name === categoryName);
    return category ? category.permissions : [];
};

/** Type-guard for checking if a string is a valid permission. */
export const isValidPermission = (permission: string): permission is PermissionName => {
    return getAllPermissions().includes(permission as PermissionName);
};

/**
 * Format a product:action permission string for display.
 * e.g. "customers:view" -> "Customers View"
 */
export const formatPermission = (permission: string): string => {
    return permission
        .split(':')
        .map(part =>
            part
                .split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
        )
        .join(' ');
};
